import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { operationService } from '../services/operationService';
import { Operation, Team, AuditLog } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEye, faChartLine } from '@fortawesome/free-solid-svg-icons';
import OperationOverview from '../components/OperationOverview';
import OperationAnalysis from '../components/OperationAnalysis';
import api from '../services/api';

const OperationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Check permissions
  const canViewAnalytics = user?.permissions?.['firehub-super-admin'] || user?.permissions?.['asm-manage-analytics'];
  const canGenerateReports = user?.permissions?.['firehub-super-admin'] || user?.permissions?.['asm-manage-reports'];
  
  const [operation, setOperation] = useState<Operation | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'analysis'>('overview');
  const [generatingReport, setGeneratingReport] = useState(false);

  useEffect(() => {
    const loadOperationData = async () => {
      if (!id || !user?.orgID) return;

      try {
        setLoading(true);
        setError('');

        // Load operation details
        const operationData = await operationService.getOperation(user.orgID, parseInt(id));
        if (operationData && operationData.length > 0) {
          setOperation(operationData[0]);
        }

        // Load teams
        const teamsData = await operationService.getTeamsByOperation(user.orgID, parseInt(id));
        setTeams(teamsData);

        // Load audit logs
        const auditLogsData = await operationService.getAuditLogsByOperation(user.orgID, parseInt(id));
        setAuditLogs(auditLogsData);

      } catch (error: any) {
        setError(error.message || 'Fehler beim Laden der Einsatzdaten');
        console.error('Error loading operation data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOperationData();
  }, [id, user?.orgID]);

  // Set active tab based on URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'analysis') {
      setActiveTab('analysis');
    } else {
      setActiveTab('overview');
    }
  }, [searchParams]);

  const handleGenerateReport = async () => {
    if (!operation || !user?.orgID) return;

    try {
      setGeneratingReport(true);
      const blob = await operationService.generateReport(user.orgID, operation.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Einsatz-${operation.opsNumber}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error generating report:', error);
      alert('Fehler beim Generieren des Berichts: ' + error.message);
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleUpdateOperation = async (updatedOperation: Partial<Operation>) => {
    if (!operation || !user?.orgID) return;

    try {
      // Prepare the update payload according to PWA API specification
      const updatePayload: any = {
        orgID: user.orgID
      };

      // Add location if provided
      if (updatedOperation.location !== undefined) {
        updatePayload.location = updatedOperation.location;
        updatePayload.ort = updatedOperation.location;
      }

      // Add category/type if provided
      if (updatedOperation.categoryID !== undefined) {
        updatePayload.type = updatedOperation.categoryID;
      }

      // Add category name if provided
      if (updatedOperation.category !== undefined) {
        updatePayload.category = updatedOperation.category;
        updatePayload.typ = updatedOperation.category;
        updatePayload.alertTitle = updatedOperation.category;
      }

      // Add title and description if both location and category are provided
      if (updatedOperation.location && updatedOperation.category) {
        updatePayload.title = `${updatedOperation.location}: ${updatedOperation.category}`;
        updatePayload.description = `${updatedOperation.category} an ${updatedOperation.location}`;
      }

      // Update local state immediately (like PWA does)
      const newLocation = updatedOperation.location !== undefined ? updatedOperation.location : operation.location;
      const newCategory = updatedOperation.category !== undefined ? updatedOperation.category : operation.category;
      const newCategoryID = updatedOperation.categoryID !== undefined ? updatedOperation.categoryID : operation.categoryID;
      
      const updatedOperationData = {
        ...operation,
        location: newLocation,
        category: newCategory,
        categoryID: newCategoryID,
        // Update title based on new location and category
        title: `${newLocation}: ${newCategory}`,
        // Update other related fields
        alertTitle: newCategory,
        typ: newCategory,
        ort: newLocation
      };
      
      setOperation(updatedOperationData);

      // Try API update - but don't fail if it errors
      try {
        const response = await api.put(`/api/v1/operations/${operation.id}`, updatePayload);
      } catch (error: any) {
        console.warn('⚠️ API Update fehlgeschlagen, aber lokal gespeichert:', error.response?.status, error.message);
        // Don't throw error - data is already saved locally
      }
      
    } catch (error: any) {
      console.error('Error updating operation:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        fontSize: '18px',
        color: '#666'
      }}>
        Einsatzdaten werden geladen...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#ffebee',
        color: '#c91f0c',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '16px'
      }}>
        <strong>Fehler:</strong> {error}
      </div>
    );
  }

  if (!operation) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        color: '#666'
      }}>
        Einsatz nicht gefunden
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '10px 15px',
            backgroundColor: '#c91f0c',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Zurück
        </button>
        <h1 style={{ margin: 0, color: '#333' }}>
          {operation.title || `${operation.location}: ${operation.alertTitle}`}
        </h1>
      </div>

      {/* Tab Navigation */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px',
        padding: '20px'
      }}>
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'overview' ? '#c91f0c' : '#f5f5f5',
              color: activeTab === 'overview' ? 'white' : '#333',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'overview' ? 'bold' : 'normal',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              boxShadow: activeTab === 'overview' ? '0 2px 4px rgba(201, 31, 12, 0.3)' : 'none'
            }}
          >
            <FontAwesomeIcon icon={faEye} />
            Übersicht & Bearbeitung
          </button>
          {canViewAnalytics && (
            <button
              onClick={() => setActiveTab('analysis')}
              style={{
                padding: '12px 24px',
                backgroundColor: activeTab === 'analysis' ? '#c91f0c' : '#f5f5f5',
                color: activeTab === 'analysis' ? 'white' : '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === 'analysis' ? 'bold' : 'normal',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: activeTab === 'analysis' ? '0 2px 4px rgba(201, 31, 12, 0.3)' : 'none'
              }}
            >
              <FontAwesomeIcon icon={faChartLine} />
              Analyse
            </button>
          )}
        </div>

        {/* Sealed Operation Info */}
        {operation.lockStatus === 2 && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              fontSize: '20px'
            }}>🔐</div>
            <div>
              <h3 style={{ margin: '0 0 4px 0', color: '#856404', fontSize: '16px', fontWeight: 'bold' }}>
                Einsatz ist versiegelt
              </h3>
              <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
                Dieser Einsatz kann nicht mehr bearbeitet werden.
              </p>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OperationOverview
            operation={operation}
            teams={teams}
            onGenerateReport={canGenerateReports ? handleGenerateReport : () => {}}
            onUpdateOperation={operation.lockStatus === 2 ? undefined : handleUpdateOperation}
          />
        )}
        
        {activeTab === 'analysis' && canViewAnalytics && (
          <OperationAnalysis
            operation={operation}
            teams={teams}
            auditLogs={auditLogs}
          />
        )}
      </div>
    </div>
  );
};

export default OperationDetail; 